import React, { useMemo } from 'react';
import {
	Container,
	Content,
	Footer,
	CardContainer,
	SectionLogo,
	SkeletonContainer,
	CardItemContainer,
} from './NewRisk.styles';
import { ReactComponent as Heart } from 'App/assets/img/heart.svg';
import {
	ActionCard,
	Checkbox,
	ErrorText,
	InputField,
	Label,
	Paragraph,
	Skeleton,
	TextArea,
	tokens,
} from '@welbe/web-design-system';
import { Button } from '@welbe/design-system';
import { useNewRisk } from './NewRisk.hooks';
import { ErrorInfo } from 'App/Components/ErrorInfo/ErrorInfo';
import { JobProfileType } from 'Views/JobProfile/JobProfile.enums';
import { useAnalyticsOpenScreen } from 'hooks/useAnalyticsOpenScreen';

export const NewRisk = () => {
	const {
		id,
		jobProfileType,
		jobRisks,
		hasError,
		step,
		risk,
		formError,
		isLoading,
		renderTitle,
		nextStep,
		prevStep,
		handleChangeRisk,
	} = useNewRisk();

	useAnalyticsOpenScreen({
		context: 'job-profile',
		trackName: id
			? `Edit a ${
					jobProfileType === JobProfileType.RISK_PROFILE
						? 'Risk Profile'
						: 'Exposure Area'
			  }`
			: `Create a ${
					jobProfileType === JobProfileType.RISK_PROFILE
						? 'Risk Profile'
						: 'Exposure Area'
			  }`,
	});

	const RenderStep0 = useMemo(() => {
		if (id && isLoading) {
			return (
				<Content width="40vw">
					<SkeletonContainer>
						<Label>Nombre</Label>
						<Skeleton />
					</SkeletonContainer>
					<SkeletonContainer>
						<Label>Descripción</Label>
						<Skeleton height="80px" />
					</SkeletonContainer>
					{jobProfileType === JobProfileType.EXPOSITION_AREA && (
						<SkeletonContainer>
							<Label>Ubicación</Label>
							<Skeleton height="80px" />
						</SkeletonContainer>
					)}
				</Content>
			);
		}

		return (
			<Content width="40vw">
				<InputField
					id="name"
					name="name"
					label="Nombre"
					placeholder="Escribe el nombre del área de exposición."
					value={risk?.name}
					error={!!formError?.name}
					errorText="Este campo es obligatorio"
					onChange={(value) => handleChangeRisk('name', value)}
				/>
				<TextArea
					id="description"
					name="description"
					label="Descripción"
					placeholder="Agrega una breve descripción del área que agregarás, las actividades que ahi se realizan y los riesgos presentes."
					value={risk?.description}
					error={!!formError?.description}
					errorMessage="Este campo es obligatorio"
					handleChange={(value) => handleChangeRisk('description', value)}
				/>
				{jobProfileType === JobProfileType.EXPOSITION_AREA && (
					<TextArea
						id="address"
						name="address"
						label="Ubicación"
						placeholder="Agrega algunas referencias de ubicación del área de exposición."
						value={risk?.address}
						error={!!formError?.address}
						errorMessage="Este campo es obligatorio"
						handleChange={(value) => handleChangeRisk('address', value)}
					/>
				)}
			</Content>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formError?.description,
		formError?.name,
		formError?.address,
		risk?.address,
		risk?.description,
		risk?.name,
	]);

	const RenderStep1 = useMemo(() => {
		return (
			<Content>
				<Paragraph>
					Selecciona los factores de riesgo a los que el colaborador está
					expuesto en función de sus actividades laborales.
				</Paragraph>
				{!!formError?.associatedRisk && (
					<ErrorText>
						Es obligatorio seleccionar al menos un factor de riesgo.
					</ErrorText>
				)}
				<CardContainer>
					{hasError ? (
						<ErrorInfo />
					) : (
						<>
							{jobRisks.map((jobRisk) => (
								<CardItemContainer key={jobRisk.id}>
									<ActionCard
										icon={<img src={jobRisk.iconUrl} width="30px" />}
										title="Riesgo"
										description={jobRisk.name}
										onClick={() =>
											handleChangeRisk('associatedRisk', jobRisk.id)
										}
										action={
											<Checkbox
												topDistance="3.5px"
												value={risk?.associatedRisk.includes(jobRisk.id)}
											/>
										}
										disabled={isLoading}
									/>
								</CardItemContainer>
							))}
						</>
					)}
				</CardContainer>
			</Content>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jobRisks, hasError, formError?.associatedRisk, risk?.associatedRisk]);

	return (
		<>
			<Container>
				<SectionLogo>
					<Heart />
				</SectionLogo>
				<Paragraph
					margin="54px 0 20px 0"
					fontSize="16px"
					fontWeight="600"
					textAlign="center"
					color={tokens.color_primary}
				>
					{renderTitle()}
				</Paragraph>
				{step === 0 && RenderStep0}
				{step === 1 && RenderStep1}
			</Container>
			<Footer>
				<Button
					variant="outline"
					width={'180px'}
					onClick={prevStep}
					disabled={isLoading}
				>
					Regresar
				</Button>
				<Button width={'180px'} onClick={nextStep} disabled={isLoading}>
					{id ? 'Guardar' : ' Continuar'}
				</Button>
			</Footer>
		</>
	);
};
